import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { API_BASE } from "@/lib/api";
import { Section, Field, Choice, FilePick, eur } from "@/components/enrollment/FormParts";
import { Landmark, CheckCircle2, Loader2, Info } from "lucide-react";

const INIT = { payment_plan: "unica", couple: false, nationality: "italiana" };
const FINAL_NOTE = "Questa è una pre-iscrizione. Dopo la verifica dei dati, la Segreteria ti invierà il modulo completo (regolamento, consensi e firma) per finalizzare l’iscrizione.";

export function calcAmount(cfg, plan, couple) {
  const base = couple ? cfg.couple_fee : cfg.fee;
  if (plan === "unica") { const t = Math.round(base * (1 - cfg.discount_pct / 100) * 100) / 100; return { total: t, first: t, n: 1 }; }
  if (plan === "mensile") { const m = cfg.monthly_amount || 50; return { total: base, first: m, n: Math.max(1, Math.ceil(base / m)) }; }
  return { total: base, first: Math.round((base / cfg.installments) * 100) / 100, n: cfg.installments };
}

export default function IscrizioneModulare() {
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState(INIT);
  const [files, setFiles] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { axios.get(`${API_BASE}/enrollments/public/config`).then((r) => setCfg(r.data)).catch(() => setCfg({ error: true })); }, []);

  if (!cfg) return <div className="min-h-screen grid place-items-center text-stone-400"><Loader2 className="animate-spin"/></div>;
  if (cfg.error || !cfg.open) return <div className="min-h-screen grid place-items-center p-6 text-center text-stone-600" data-testid="enr-closed">Le pre-iscrizioni al Percorso Modulare al momento sono chiuse.</div>;
  const amt = calcAmount(cfg, f.payment_plan, f.couple);
  const reason = (cfg.payment_reason || "").replace("{anno}", cfg.academic_year).replace("{cognome}", f.last_name || "Cognome").replace("{nome}", f.first_name || "Nome");

  const submit = async (e) => {
    e.preventDefault();
    const italian = f.nationality === "italiana";
    if (!(files.id_doc || []).length || (italian && !(files.cf_doc || []).length)) return toast.error(italian ? "Carica documento d'identità e codice fiscale" : "Carica il documento d'identità");
    setSending(true);
    try {
      const { data } = await axios.post(`${API_BASE}/enrollments/public`, f);
      for (const [kind, list] of Object.entries(files)) {
        for (const file of list) {
          const fd = new FormData(); fd.append("kind", kind); fd.append("file", file);
          await axios.post(`${API_BASE}/enrollments/public/${data.id}/files`, fd, { params: { token: data.upload_token } })
            .catch((er) => toast.error(`${file.name}: ${er.response?.data?.detail || "caricamento non riuscito"}`));
        }
      }
      setDone(data); window.scrollTo(0, 0);
    } catch (er) {
      const d = er.response?.data?.detail;
      toast.error(Array.isArray(d) ? `Controlla il campo: ${d[0]?.loc?.slice(-1)[0]}` : d || "Invio non riuscito");
    } finally { setSending(false); }
  };

  if (done) return <EnrollmentDone cfg={cfg} amt={amt} reason={reason}/>;

  return (
    <div className="min-h-screen bg-[#F7F4EE]" data-testid="enrollment-public-page">
      <Toaster richColors position="top-center"/>
      <header className="bg-[#1C352D] text-white">
        <div className="max-w-3xl mx-auto px-5 py-10 sm:py-14">
          <div className="text-[11px] uppercase tracking-[0.25em] text-[#E8B48A]">Scuola Missionaria Ramo di Mandorlo</div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3 leading-tight" data-testid="enr-title">Pre-iscrizione al Percorso Modulare</h1>
          <p className="mt-3 text-white/75 text-sm sm:text-base">Anno Accademico {cfg.academic_year}. Compila i tuoi dati e scegli la modalità di pagamento: la Segreteria ti contatterà per completare l’iscrizione.</p>
        </div>
      </header>
      <form onSubmit={submit} className="max-w-3xl mx-auto px-4 sm:px-5 py-8 space-y-5">
        <input type="text" name="website" className="hidden" tabIndex={-1} autoComplete="off" onChange={(e) => set("website", e.target.value)}/>
        <Section n={1} title="Dati dello studente" testId="enr-sec-1">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Cognome" k="last_name" f={f} set={set} required/>
            <Field label="Nome" k="first_name" f={f} set={set} required/>
            <Field label="Nato/a a" k="birth_place" f={f} set={set} required/>
            <Field label="Provincia di nascita" k="birth_province" f={f} set={set}/>
            <Field label="Data di nascita" k="date_of_birth" type="date" f={f} set={set} required/>
            <div>
              <label className="text-xs text-stone-600">Cittadinanza<span className="text-[#C2410C]"> *</span></label>
              <select value={f.nationality} onChange={(e) => set("nationality", e.target.value)} data-testid="enr-nationality"
                className="mt-1.5 w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm">
                <option value="italiana">Italiana</option>
                <option value="estera">Estera (es. Svizzera, Germania…)</option>
              </select>
            </div>
            {f.nationality === "estera"
              ? <Field label="Paese di cittadinanza" k="country" f={f} set={set} required/>
              : <Field label="Codice fiscale" k="codice_fiscale" f={f} set={set} required/>}
            {f.nationality === "estera" && <Field label="Codice fiscale italiano (facoltativo, se in possesso)" k="codice_fiscale" f={f} set={set} className="sm:col-span-2"/>}
            <Field label="Residenza in Via / Piazza e n. civico" k="address" f={f} set={set} required className="sm:col-span-2"/>
            <Field label="CAP" k="cap" f={f} set={set} required/>
            <Field label="Città" k="city" f={f} set={set} required/>
            <Field label="Provincia" k="province" f={f} set={set}/>
            <Field label="Domicilio (se diverso dalla residenza)" k="domicile" f={f} set={set}/>
            <Field label="Telefono" k="phone" type="tel" f={f} set={set} required/>
            <Field label="E-mail" k="email" type="email" f={f} set={set} required/>
            <Field label="Titolo di studio" k="education" f={f} set={set}/>
          </div>
        </Section>
        <PaymentSection cfg={cfg} f={f} set={set} amt={amt} reason={reason}/>
        <Section n={3} title="Documentazione" testId="enr-sec-3">
          <FilePick kind="id_doc" label="Documento d’identità in corso di validità" files={files} setFiles={setFiles} required/>
          <FilePick kind="cf_doc" label={f.nationality === "estera" ? "Codice fiscale o tessera sanitaria (facoltativo)" : "Codice fiscale o tessera sanitaria"} files={files} setFiles={setFiles} required={f.nationality !== "estera"}/>
          <FilePick kind="other" label="Altri documenti (facoltativo)" files={files} setFiles={setFiles}/>
        </Section>
        <Section n={4} title="Informazioni aggiuntive" testId="enr-sec-4">
          <Field label="Informazioni o esigenze da comunicare alla Direzione (facoltativo)" k="additional_info" area f={f} set={set}/>
        </Section>
        <div className="rounded-2xl border border-[#1C352D]/20 bg-white p-5 text-sm text-stone-700 flex gap-3" data-testid="enr-final-note">
          <Info size={18} className="text-[#1C352D] shrink-0 mt-0.5"/>
          <p>{FINAL_NOTE} I dati inseriti sono usati solo per gestire la tua pre-iscrizione.</p>
        </div>
        <Button type="submit" disabled={sending} data-testid="enr-submit" className="w-full h-12 rounded-full bg-[#C2410C] hover:bg-[#9A3412] text-white text-base">
          {sending ? <><Loader2 size={16} className="animate-spin mr-2"/> Invio in corso…</> : "Invia pre-iscrizione"}
        </Button>
      </form>
    </div>
  );
}

function BankBox({ cfg, amt, reason }) {
  const first = amt.n > 1 ? "Prima rata" : "Importo";
  return (
    <div className="rounded-xl bg-[#1C352D] text-white p-4 sm:p-5 space-y-1.5 text-sm" data-testid="enr-bank-box">
      <div className="flex items-center gap-2 text-[#E8B48A] text-[11px] uppercase tracking-widest"><Landmark size={13}/> Bonifico bancario</div>
      {cfg.iban ? <>
        <div><span className="text-white/60">IBAN</span> <b className="font-mono tracking-wide break-all" data-testid="enr-iban">{cfg.iban}</b></div>
        <div><span className="text-white/60">Intestatario</span> <b data-testid="enr-holder">{cfg.account_holder}</b></div>
        {cfg.bank_name && <div><span className="text-white/60">Banca</span> {cfg.bank_name}</div>}
        <div><span className="text-white/60">Causale</span> {reason}</div>
      </> : <div className="text-white/70" data-testid="enr-bank-pending">Le coordinate bancarie ti saranno comunicate dalla Segreteria.</div>}
      <div className="pt-1"><span className="text-white/60">{first}</span> <b className="text-lg" data-testid="enr-first-payment">{eur(amt.first)}</b>
        {amt.n > 1 && <span className="text-white/60"> · {amt.n} versamenti</span>}</div>
    </div>
  );
}

function PaymentSection({ cfg, f, set, amt, reason }) {
  const m = calcAmount(cfg, "mensile", f.couple);
  return (
    <Section n={2} title="Quota di iscrizione e modalità di pagamento" testId="enr-sec-2">
      <p className="text-sm text-stone-600">La quota di iscrizione è di <b>{eur(cfg.fee)}</b> per anno accademico. L’iscrizione vale per l’intero anno accademico.</p>
      <Choice name="plan" value={f.payment_plan} onChange={(v) => set("payment_plan", v)} options={[
        ["unica", `Unica soluzione – con sconto del ${cfg.discount_pct}%`, `${eur(calcAmount(cfg, "unica", f.couple).total)} in un solo versamento`],
        ["rate", `${cfg.installments} rate`, `${cfg.installments} versamenti da ${eur(calcAmount(cfg, "rate", f.couple).first)}, secondo le scadenze comunicate dalla Segreteria`],
        ["mensile", `Rata mensile da ${eur(cfg.monthly_amount || 50)}`, `${m.n} versamenti mensili da ${eur(m.first)}`]]}/>
      <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="enr-couple">
        <input type="checkbox" checked={f.couple} onChange={(e) => set("couple", e.target.checked)} className="mt-1 accent-[#1C352D] w-4 h-4"/>
        <span>Iscrizione in coppia – quota ridotta di {eur(cfg.couple_fee)} per il coniuge iscritto insieme</span>
      </label>
      {f.couple && <Field label="Nome e cognome del coniuge" k="spouse_name" f={f} set={set} required/>}
      <div className="text-sm"><span className="text-stone-500">Metodo di pagamento:</span> <b>Bonifico bancario</b></div>
      <BankBox cfg={cfg} amt={amt} reason={reason}/>
    </Section>
  );
}

function EnrollmentDone({ cfg, amt, reason }) {
  return (
    <div className="min-h-screen bg-[#F7F4EE] grid place-items-center p-5" data-testid="enr-done">
      <div className="max-w-lg w-full bg-white border border-stone-200 rounded-2xl p-7 space-y-5">
        <CheckCircle2 className="text-emerald-600" size={40}/>
        <h1 className="font-display text-3xl font-semibold text-[#1C352D]">Pre-iscrizione inviata</h1>
        <p className="text-sm text-stone-600">Grazie! {FINAL_NOTE}</p>
        <BankBox cfg={cfg} amt={amt} reason={reason}/>
      </div>
    </div>
  );
}
